'use strict';

import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState
} from 'react';
import {createPortal} from 'react-dom';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';

import {
    Button,
    ButtonProps
} from 'primereact/button';


type InputProps = Omit<ButtonProps, 'onClick' | 'key' | 'content'>;
const inputProps = keys<InputProps>();

type ButtonBox = {
    x : number;
    y : number;
    width : number;
    height : number;
};

type DefaultProps = {
    horizontalContentAlign? : 'left' | 'right';
    verticalContentAlign? : 'top' | 'bottom'; // TODO: add 'center' and implement

};
const defaultProps : Required<DefaultProps> = {
    horizontalContentAlign: 'right',
    verticalContentAlign  : 'bottom'
};

type Props = {
    id : string;

    /**
     * The content of the expanded button or a callback returning the content.
     */
    content : React.ReactNode | ( () => React.ReactNode );

    buttonClassName? : string;
    contentClassName? : string;

    /**
     * Callback if the button will expand.
     * Return false to avoid expanding.
     */
    onExpand? : ( event : React.MouseEvent<HTMLButtonElement> ) => boolean;

    /**
     * Callback if the button will collapse.
     * Return false to avoid collapsing.
     */
    onCollapse? : ( event : React.MouseEvent, isOutsideClicked : boolean, eventPhase : 'beforeClick' | 'afterClick' | 'collapse' ) => boolean;
};

export type ComponentProps = InputProps & DefaultProps & Props;

type AllProps = InputProps & Required<DefaultProps> & Props;

export type ReferenceType = {
    expand : ( event : React.MouseEvent ) => void;
    collapse : ( event : React.MouseEvent ) => void;
};

const ContentButton = React.forwardRef<ReferenceType, AllProps>( ( props : AllProps, forwardedRef ) => {
    const {
              buttonClassName,
              content,
              contentClassName,
              horizontalContentAlign,
              id,
              onCollapse,
              onExpand,
              verticalContentAlign
          }                                     = props,
          button                                = useRef<Button>( null ),
          buttonBox                             = useRef<ButtonBox | null>( null ),
          overlayElement                        = useRef<HTMLDivElement | null>( null ),
          cContentVisible                       = useRef<boolean>( false ),
          cJustAdded                            = useRef<boolean>( false ),
          cProceedBefore                        = useRef<boolean>( true ),
          animationRunning                      = useRef<boolean>( false ),
          cContent                              = useRef<React.ReactNode | undefined>( typeof content === 'function' ? undefined : content ),
          [ contentVisible, setContentVisible ] = useState<boolean>( false ),
          [ rerenderToggle, setRerenderToggle ] = useState<number>( 0 ),
          expand                                = useCallback(
              ( event : React.MouseEvent ) => {
                  let proceed = true;

                  if ( onExpand ) {
                      proceed = onExpand( event as React.MouseEvent<HTMLButtonElement> );
                  }

                  if ( proceed ) {
                      setContentVisible( true );
                  }
              },
              [
                  onExpand
              ]
          ),
          collapse                              = useCallback(
              ( event : React.MouseEvent ) => {
                  let proceed = true;

                  if ( onCollapse ) {
                      proceed = onCollapse( event as React.MouseEvent, proceed, 'collapse' );
                  }

                  if ( proceed ) {
                      setContentVisible( false );
                  }
              },
              [
                  onCollapse
              ]
          ),
          onButtonClick                         = useCallback(
              ( event : React.MouseEvent<HTMLButtonElement> | MouseEvent ) => {
                  if ( !cJustAdded.current ) {
                      let proceed = true;

                      if ( !cContentVisible.current && onExpand ) {
                          proceed = onExpand( event as React.MouseEvent<HTMLButtonElement> );
                      }

                      if ( proceed ) {
                          setContentVisible( !cContentVisible.current );
                      }
                  }
              },
              [
                  onExpand
              ]
          ),
          onDocumentMouseDownBefore             = useCallback(
              ( event : MouseEvent ) => {
                  if ( !cJustAdded.current ) {
                      let proceed : boolean = false;

                      if (
                          overlayElement.current
                          && event.target
                          && (
                              event.target === overlayElement.current
                              || !overlayElement.current.contains( event.target as Node )
                          )
                      ) {
                          proceed = true;
                      }

                      if ( cContentVisible.current && onCollapse ) {
                          proceed = onCollapse( event as unknown as React.MouseEvent, proceed, 'beforeClick' );
                      }

                      cProceedBefore.current = proceed;
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onDocumentMouseDownAfter              = useCallback(
              ( event : MouseEvent ) => {
                  if ( !cJustAdded.current && cProceedBefore.current ) {
                      let proceed : boolean = false;

                      if (
                          overlayElement.current
                          && event.target
                          && (
                              event.target === overlayElement.current
                              || !overlayElement.current.contains( event.target as Node )
                          )
                      ) {
                          proceed = true;
                      }

                      if ( cContentVisible.current && onCollapse ) {
                          proceed = onCollapse( event as unknown as React.MouseEvent, proceed, 'afterClick' );
                      }

                      if ( proceed ) {
                          setContentVisible( false );
                          document.removeEventListener( 'click', onDocumentMouseDownBefore, true );
                          document.removeEventListener( 'click', onDocumentMouseDownAfter, false );
                      }
                  }

                  cProceedBefore.current = true;
              },
              [
                  'onlyOnce'
              ]
          );

    cContentVisible.current = contentVisible;
    if ( typeof content !== 'function' ) {
        cContent.current = content;
    }

    useImperativeHandle(
        forwardedRef,
        () => ( {
            expand,
            collapse
        } )
    );

    // on mount / unmount
    useEffect(
        () => {
            const existingOverlayElement = document.getElementById( `${id}_overlay` );

            if ( existingOverlayElement ) {
                document.body.removeChild( existingOverlayElement );
            }

            // unmount
            return () => {
                if ( overlayElement.current ) {
                    document.body.removeChild( overlayElement.current );
                }

                document.removeEventListener( 'click', onDocumentMouseDownBefore, true );
                document.removeEventListener( 'click', onDocumentMouseDownAfter, false );
            }
        },
        []
    );

    // compute current buttonBox
    useLayoutEffect(
        () => {
            if ( button.current ) {
                const buttonElement = document.getElementById( id );

                if ( buttonElement ) {

                    const box                      = buttonElement.getBoundingClientRect(),
                          newButtonBox : ButtonBox = {
                              x     : box.x,
                              y     : box.y,
                              width : box.width,
                              height: box.height
                          };

                    // button moved -> hide content
                    if ( buttonBox.current?.x !== newButtonBox.x || buttonBox.current?.y !== newButtonBox.y ) {
                        setContentVisible( false );
                    }

                    buttonBox.current = newButtonBox;
                }
            }
            else {
                setTimeout(
                    () => {
                        setRerenderToggle( rerenderToggle + 1 );
                    },
                    10
                );
            }
        }
    );

    // render content
    useLayoutEffect(
        () => {
            if ( button.current && contentVisible ) {
                const buttonElement       = document.getElementById( id ) as HTMLElement,
                      buttonElementStyle  = window.getComputedStyle( buttonElement ),
                      overlayButton       = document.createElement( 'div' ),
                      overlayButtonButton = buttonElement.cloneNode( true ) as HTMLElement,
                      overlayContent      = document.createElement( 'div' );

                let horizontalContentAlignToUse = horizontalContentAlign,
                    verticalContentAlignToUse   = verticalContentAlign,
                    buttonWidth : string        = buttonElementStyle.getPropertyValue( 'width' ),
                    buttonHeight : string       = buttonElementStyle.getPropertyValue( 'height' );

                overlayElement.current = document.createElement( 'div' );
                overlayElement.current.id = `${id}_overlay`;
                overlayElement.current.className = `yn-contentbutton-overlay yn-contentbutton-overlay-horizontal-${horizontalContentAlignToUse} yn-contentbutton-overlay-vertical-${verticalContentAlignToUse}`;
                overlayElement.current.style.left = `calc( ${buttonBox.current!.x}px - .25rem )`;
                overlayElement.current.style.top = `calc( ${buttonBox.current!.y}px - .25rem )`;

                if ( !buttonElement.classList.contains( 'p-button-icon-only' ) ) {
                    const numWidth     = parseFloat( buttonWidth.replace( /[^0-9\.]+$/, '' ) ),
                          numHeight    = parseFloat( buttonHeight.replace( /[^0-9\.]+$/, '' ) ),
                          numLRPadding = parseFloat( buttonElementStyle.getPropertyValue( 'padding-left' ).replace( /[^0-9\.]+$/, '' ) ) + parseFloat( buttonElementStyle.getPropertyValue( 'padding-right' ).replace( /[^0-9\.]+$/, '' ) ),
                          numLRBorder  = parseFloat( buttonElementStyle.getPropertyValue( 'border-left-width' ).replace( /[^0-9\.]+$/, '' ) ) + parseFloat( buttonElementStyle.getPropertyValue( 'border-right-width' ).replace( /[^0-9\.]+$/, '' ) ),
                          numTBPadding = parseFloat( buttonElementStyle.getPropertyValue( 'padding-top' ).replace( /[^0-9\.]+$/, '' ) ) + parseFloat( buttonElementStyle.getPropertyValue( 'padding-bottom' ).replace( /[^0-9\.]+$/, '' ) ),
                          numTBBorder  = parseFloat( buttonElementStyle.getPropertyValue( 'border-top-width' ).replace( /[^0-9\.]+$/, '' ) ) + parseFloat( buttonElementStyle.getPropertyValue( 'border-bottom-width' ).replace( /[^0-9\.]+$/, '' ) );

                    buttonWidth = `${numWidth + numLRPadding + numLRBorder}px`;
                    buttonHeight = `${numHeight + numTBPadding + numTBBorder}px`;
                }

                overlayButton.id = `${id}_overlay_button`;
                overlayButton.className = `yn-contentbutton-overlay-button ${buttonClassName}`;
                overlayButton.style.width = buttonWidth;//buttonElementStyle.getPropertyValue( 'width' );
                overlayButton.style.height = buttonHeight;//buttonElementStyle.getPropertyValue( 'height' );
                overlayButton.style.borderBottomLeftRadius = buttonElementStyle.getPropertyValue( 'border-bottom-left-radius' );
                overlayButton.style.borderBottomRightRadius = buttonElementStyle.getPropertyValue( 'border-bottom-right-radius' );
                overlayButton.style.borderTopLeftRadius = buttonElementStyle.getPropertyValue( 'border-top-left-radius' );
                overlayButton.style.borderTopRightRadius = buttonElementStyle.getPropertyValue( 'border-top-right-radius' );

                overlayButtonButton.style.width = buttonWidth;//buttonElementStyle.getPropertyValue( 'width' );
                overlayButtonButton.style.height = buttonHeight;//buttonElementStyle.getPropertyValue( 'height' );
                overlayButtonButton.style.paddingLeft = buttonElement.classList.contains( 'p-button-icon-only' ) ? '0' : buttonElementStyle.getPropertyValue( 'padding-left' );
                overlayButtonButton.style.paddingRight = buttonElement.classList.contains( 'p-button-icon-only' ) ? '0' : buttonElementStyle.getPropertyValue( 'padding-right' );
                overlayButtonButton.style.paddingTop = buttonElement.classList.contains( 'p-button-icon-only' ) ? '0' : buttonElementStyle.getPropertyValue( 'padding-top' );
                overlayButtonButton.style.paddingBottom = buttonElement.classList.contains( 'p-button-icon-only' ) ? '0' : buttonElementStyle.getPropertyValue( 'padding-bottom' );
                overlayButtonButton.onclick = onButtonClick;

                overlayContent.id = `${id}_overlay_content`;
                overlayContent.className = `yn-contentbutton-overlay-content ${contentClassName}`;

                overlayButton.append( overlayButtonButton );

                if ( verticalContentAlign === 'bottom' ) {
                    overlayElement.current.append( overlayButton );
                    overlayElement.current.append( overlayContent );
                }
                else {
                    overlayElement.current.append( overlayContent );
                    overlayElement.current.append( overlayButton );
                }

                document.body.appendChild( overlayElement.current );

                if ( typeof content === 'function' ) {
                    cContent.current = content();
                }

                animationRunning.current = true;
                setRerenderToggle( rerenderToggle + 1 );

                // defer first part of animation ( button tween ) until content has been rendered
                setTimeout(
                    () => {
                        overlayContent.style.display = 'block';
                        overlayContent.style.width = '0px';

                        const contentHeight = overlayContent.scrollHeight,
                              contentWidth  = overlayContent.scrollWidth;

                        overlayContent.style.width = '';

                        // render content on screen
                        if ( horizontalContentAlignToUse === 'right' && buttonBox.current!.x + contentWidth >= document.body.clientWidth ) {
                            horizontalContentAlignToUse = 'left';
                        }
                        else if ( horizontalContentAlignToUse === 'left' && buttonBox.current!.x + buttonBox.current!.width - contentWidth <= 0 ) {
                            horizontalContentAlignToUse = 'right';
                        }
                        if ( verticalContentAlignToUse === 'bottom' && buttonBox.current!.y + buttonBox.current!.height + contentHeight >= document.body.clientHeight ) {
                            verticalContentAlignToUse = 'top';
                            overlayElement.current?.insertBefore( overlayContent, overlayButton );
                        }
                        else if ( verticalContentAlignToUse === 'top' && buttonBox.current!.y - contentHeight <= 0 ) {
                            verticalContentAlignToUse = 'bottom';
                            overlayElement.current?.insertBefore( overlayButton, overlayContent );
                        }

                        overlayElement.current!.className = `yn-contentbutton-overlay yn-contentbutton-overlay-horizontal-${horizontalContentAlignToUse} yn-contentbutton-overlay-vertical-${verticalContentAlignToUse} enter`;
                        overlayButton.style.width = `calc( ${buttonWidth} + .5rem )`;
                        overlayButton.style.height = `calc( ${buttonHeight} + .5rem )`;

                        // defer second part of animation ( content expand ) until first part has finished
                        setTimeout(
                            () => {
                                if ( overlayElement.current ) {
                                    overlayContent.style.height = `${contentHeight}px`;

                                    if ( horizontalContentAlignToUse === 'left' ) {
                                        overlayElement.current.style.left = `calc( ${buttonBox.current!.x + buttonBox.current!.width - contentWidth}px - .25rem )`;
                                        overlayButton.style.left = `${contentWidth - buttonBox.current!.width}px`;
                                    }

                                    if ( verticalContentAlignToUse === 'bottom' ) {
                                        overlayElement.current.style.top = `calc( ${buttonBox.current!.y}px - .25rem )`;
                                    }
                                    else {
                                        overlayElement.current.style.top = `calc( ${buttonBox.current!.y - contentHeight}px - .25rem )`;
                                    }

                                    // defer end up part of animation until second part has finished
                                    setTimeout(
                                        () => {
                                            overlayContent.style.overflow = 'visible';
                                            animationRunning.current = false;
                                        },
                                        200
                                    );
                                }
                                else {
                                    animationRunning.current = false;
                                }
                            },
                            100
                        );
                    },
                    11
                );

                cJustAdded.current = true;

                setTimeout(
                    () => {
                        cJustAdded.current = false;
                    },
                    25
                );

                document.addEventListener( 'click', onDocumentMouseDownBefore, true );
                document.addEventListener( 'click', onDocumentMouseDownAfter, false );
            }
            else {
                if ( overlayElement.current ) {
                    overlayElement.current.classList.remove( 'enter' );
                    overlayElement.current.classList.add( 'leave' );

                    setTimeout(
                        () => {
                            if ( overlayElement.current ) {
                                try {
                                    document.body.removeChild( overlayElement.current );
                                }
                                catch ( e ) {
                                    // do nothing
                                }
                            }

                            overlayElement.current = null;
                        },
                        300
                    );
                }
            }
        },
        [
            button.current,
            contentVisible
        ]
    );

    return <>
        <Button
            {..._.pick( props, inputProps ) as InputProps}

            id={id}
            ref={button}

            className={`yn-contentbutton ${props.className}`}
            onClick={onButtonClick}
        />
        {cContent.current && document.querySelector( `#${id}_overlay .yn-contentbutton-overlay-content` ) &&
            createPortal(
                cContent.current,
                document.querySelector( `#${id}_overlay .yn-contentbutton-overlay-content` )!
            )
        }
    </>;
} );
ContentButton.defaultProps = defaultProps;
ContentButton.displayName = 'ContentButton';

export default ContentButton;
